import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import {
  StatusChip,
  type StatusChipVariant,
} from '@/components/legacy-ui/StatusChip';
import type { HuntStatus } from '@/types';

interface HuntStatusMeta {
  label: string;
  variant: StatusChipVariant;
  icon: ReactNode;
}

const STATUS_META: Record<HuntStatus, HuntStatusMeta> = {
  active: {
    label: 'Active',
    variant: 'info',
    icon: <PlayCircleIcon className="size-3 shrink-0" />,
  },
  paused: {
    // Use the neutral "muted" variant — paused is operator state, not
    // a severity grade. The high-severity orange we used previously
    // collided with the platform's severity semantics on screens that
    // mix hunts with risks (the Findings tab).
    label: 'Paused',
    variant: 'muted',
    icon: <PauseCircleIcon className="size-3 shrink-0" />,
  },
  expired: {
    // Relabeled to "Completed" — expired hunts reached their natural
    // end just like completed ones. ExclamationTriangleIcon replaced
    // with CheckCircleIcon to match. Renders identically to completed.
    label: 'Completed',
    variant: 'muted',
    icon: <CheckCircleIcon className="size-3 shrink-0" />,
  },
  errored: {
    label: 'Errored',
    variant: 'critical',
    icon: <ExclamationCircleIcon className="size-3 shrink-0" />,
  },
  completed: {
    // variant changed from 'neutral' to 'muted' — neutral fails WCAG AA
    // contrast at 2.54:1 in light mode; muted passes at 18:1/15:1.
    label: 'Completed',
    variant: 'muted',
    icon: <CheckCircleIcon className="size-3 shrink-0" />,
  },
  stopped: {
    // Manual operator stop — distinct from natural completion (completed/expired).
    // Uses Guard red (critical) to signal intentional termination.
    label: 'Stopped',
    variant: 'critical',
    icon: <StopCircleIcon className="size-3 shrink-0" />,
  },
};

interface HuntStatusChipProps {
  status: HuntStatus;
  tooltip?: string;
  withIcon?: boolean;
}

export function HuntStatusChip({
  status,
  tooltip,
  withIcon = true,
}: HuntStatusChipProps) {
  const meta = STATUS_META[status];
  return (
    <StatusChip
      label={meta.label}
      variant={meta.variant}
      icon={withIcon ? meta.icon : undefined}
      tooltip={tooltip}
    />
  );
}
